/* eslint-disable react/no-children-prop */
import React, { useEffect, useState } from 'react'
import { ReactMarkdown } from 'react-markdown/lib/react-markdown'
import { useParams } from 'react-router-dom'

import {
	Paragraph,
	Title,
	FilePDF,
	Switch,
	BreadCrumb,
	CourseComment,
	CourseReportError,
} from '@components/atoms'
import { questionStyles } from '@components/molecules/Question/styles'
import {
	Chat16Regular,
	ErrorCircle16Regular,
	IosArrowLtr24Regular,
	IosArrowRtl24Regular,
} from '@fluentui/react-icons'
import { useIsMobile } from '@hooks'
import { ModulesProps } from '@models/course.model'
import { coursesAPI } from '@services/CoursesAPI'
import rehypeRaw from 'rehype-raw'

import { CoursesStyles } from './style'

const {
	container,
	title,
	content,
	checkbox,
	contentLeft,
	containerMobileLink,
	LinkButton,
	containerShow,
	containerTitleShow,
	containerFiles,
	containerDescription,
	containerActionBar,
} = CoursesStyles

const { ActionBar, Icon } = questionStyles

export const MyCoursesShow = () => {
	const [modules, setModules] = useState<ModulesProps | undefined>()
	const [isFinished, setIsFinished] = useState(false)
	const [actionBarValue, setActionBarValue] = useState('')

	const [getAllClientModules, { data }] =
		coursesAPI.useGetAllClientModulesMutation()

	const [
		getModuleContentShow,
		{ data: dataContentShow, isSuccess: isSuccessContentShow },
	] = coursesAPI.useGetModuleContentShowMutation()

	const [postModuleContentFinished] =
		coursesAPI.usePostContentFinishedMutation()

	const isMobile = useIsMobile()
	const { id, idModule } = useParams()

	const handleActionBar = (name: string) => {
		if (name === actionBarValue) {
			setActionBarValue('')
		} else {
			setActionBarValue(name)
		}
	}

	useEffect(() => {
		if (id && idModule) {
			getAllClientModules({})
			getModuleContentShow(idModule)
		}
	}, [getAllClientModules, getModuleContentShow, id, idModule])

	useEffect(() => {
		if (data) {
			setModules(dataContentShow?.data.module)
		}
	}, [data, dataContentShow])

	useEffect(() => {
		if (dataContentShow?.data.isFinished !== undefined) {
			setIsFinished(Boolean(dataContentShow?.data.isFinished))
		}
	}, [dataContentShow?.data.isFinished])

	useEffect(() => {
		if (isFinished !== dataContentShow?.data.isFinished && dataContentShow) {
			postModuleContentFinished({
				contentId: dataContentShow?.data.id,
				isFinished,
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFinished, postModuleContentFinished])

	return (
		<div className={container()}>
			<BreadCrumb
				buttonReturn
				locations={[
					{ name: 'Cursos', link: '/cursos' },
					{
						name: `${(modules && modules.name) || ''}`,
						link: `/cursos/meus-cursos/${id}`,
					},
					{
						name: `${(dataContentShow && dataContentShow?.data.name) || ''}`,
						link: '',
					},
				]}
			/>
			<div className={title()}>
				<Title variant='h1' color='$neutral160'>
					{(modules && modules.name) || ''}
				</Title>
			</div>
			<div className={content()}>
				{isMobile ? (
					<>
						<div className={contentLeft()}>
							<div className={containerMobileLink()}>
								<LinkButton
									to={`/cursos/meus-cursos/${id}/show/${
										dataContentShow && dataContentShow.back?.id
									}`}
									disabled={dataContentShow && !dataContentShow.back?.id}
								>
									<button type='button' className='buttonL'>
										<IosArrowLtr24Regular />
									</button>
									<Paragraph paragraphType='p1' css={{ color: '$blue20' }}>
										Anterior
									</Paragraph>
								</LinkButton>
								<LinkButton
									to={`/cursos/meus-cursos/${id}/show/${
										dataContentShow && dataContentShow.next?.id
									}`}
									disabled={dataContentShow && !dataContentShow.next?.id}
								>
									<Paragraph paragraphType='p1' css={{ color: '$blue20' }}>
										Próximo
									</Paragraph>
									<button type='button' className='buttonR'>
										<IosArrowRtl24Regular />
									</button>
								</LinkButton>
							</div>

							<div className={checkbox()}>
								<p>Conteúdo concluído</p>
								<Switch enabled={isFinished} setEnabled={setIsFinished} />
							</div>
						</div>
					</>
				) : (
					<>
						<LinkButton
							to={`/cursos/meus-cursos/${id}/show/${
								dataContentShow &&
								dataContentShow.back &&
								dataContentShow.back.id
							}`}
							disabled={dataContentShow && !dataContentShow.back}
						>
							<button type='button' className='buttonL'>
								<IosArrowLtr24Regular />
							</button>
							<Paragraph paragraphType='p1' css={{ color: '$blue20' }}>
								{dataContentShow &&
									dataContentShow.back &&
									dataContentShow.back.name}
							</Paragraph>
						</LinkButton>

						<LinkButton
							to={`/cursos/meus-cursos/${id}/show/${
								dataContentShow &&
								dataContentShow.next &&
								dataContentShow.next.id
							}`}
							disabled={dataContentShow && !dataContentShow.next}
						>
							<Paragraph paragraphType='p1' css={{ color: '$blue20' }}>
								{dataContentShow &&
									dataContentShow.next &&
									dataContentShow.next.name}
							</Paragraph>
							<button type='button' className='buttonR'>
								<IosArrowRtl24Regular />
							</button>
						</LinkButton>
					</>
				)}
			</div>

			{isSuccessContentShow && dataContentShow && (
				<div className={containerShow()}>
					<div className={containerTitleShow()}>
						<Title variant='h2' color='$neutral150'>
							{dataContentShow.data.name}
						</Title>

						{!isMobile && (
							<div className={checkbox()}>
								<p>Conteúdo concluído</p>
								<Switch enabled={isFinished} setEnabled={setIsFinished} />
							</div>
						)}
					</div>

					<div className={containerDescription()}>
						<ReactMarkdown
							rehypePlugins={[rehypeRaw]}
							children={`${dataContentShow.data.description}` || ''}
						/>
					</div>

					{dataContentShow.data.files.length > 0 && (
						<div className={containerFiles()}>
							{dataContentShow.data.files.map((file) => (
								<FilePDF
									id={file.id}
									library_id={file.library_id}
									file={file.file}
									name={file.name}
									url={file.url}
									key={file.id}
									downloaded={file.downloaded}
								/>
							))}
						</div>
					)}
				</div>
			)}

			{isSuccessContentShow && (
				<>
					<div className={containerActionBar()}>
						<ActionBar
							selected={actionBarValue === 'comment'}
							onClick={() => {
								handleActionBar('comment')
							}}
						>
							<Icon>
								<Chat16Regular />
							</Icon>
							<p>Comentários</p>
						</ActionBar>

						<ActionBar
							selected={actionBarValue === 'error'}
							onClick={() => {
								handleActionBar('error')
							}}
						>
							<Icon>
								<ErrorCircle16Regular />
							</Icon>
							<p>Reportar erro</p>
						</ActionBar>
					</div>

					{actionBarValue === 'comment' && (
						<CourseComment id={Number(idModule) || 0} />
					)}
					{actionBarValue === 'error' && (
						<CourseReportError id={Number(idModule) || 0} />
					)}
				</>
			)}
		</div>
	)
}
